/**
 * measure — the count noun shown after a ShoppingListItem's quantity (the
 * `quantityFlag`: `· 1 bottle`, `· 1 loaf`, `· 1 dozen`).
 *
 * Pure + portable (CP1: no platform imports). The word comes straight from the
 * item — an explicit per-trip `quantity_unit` count noun. It is NEVER the size
 * unit: the resolved measurement (`4 L`, `12 × 20 g`) lives ONLY in the size
 * badge / brandLine (`formatResolvedMeasurement`). When no count noun is set the
 * quantity flag is a bare multiplier (`×N`) — the caller composes that; this
 * function returns the noun or `null`.
 *
 * T36.3 / ADR-015 — the legacy `preferred_package_size_*` source was retired, and
 * the size-unit fallback was dropped (it double-rendered the size: `· 1 L` beside
 * a `4 L` badge). The resolved measurement snapshot is the single SIZE source;
 * the quantity flag is a count noun or `×N`, never a size unit.
 */

// The subset this needs — keeps the function easy to unit-test without building a
// full item, and decoupled from the rest of the row shape.
export interface ItemMeasureFields {
  quantity_unit?: string | null;
}

/**
 * The count noun shown after the quantity, or `null` when none is set.
 *
 *   - `quantity_unit` set (a per-trip count noun like `bottle` / `loaf` /
 *     `dozen`) → verbatim (no auto-pluralization, V1).
 *   - else → `null` (the caller renders a bare `×N` multiplier).
 *
 * No size unit, no metric/imperial relabel — the resolved measurement renders
 * once in the size badge, never here.
 */
export function formatItemMeasure(item: ItemMeasureFields): string | null {
  const explicit = item.quantity_unit?.trim();
  if (explicit) return explicit;
  return null;
}
